import { Component, OnInit, OnDestroy } from '@angular/core';
import { load } from '@cashfreepayments/cashfree-js';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaymentStatusModalComponent } from '../sharedComponents/payment-status-modal/payment-status-modal.component';
import { WalletinfoService } from 'src/app/_service/walletinfo.service';
import { CustomControlsService } from 'src/app/_service/custom-controls.service';
import { URLS } from 'src/app/constants/URLS';

declare var Cashfree: any;

interface Transaction {
  eventType: string;
  transactionCreatedAt: string;
  amount: number;
  transactionCurrency: string;
  transactionStatus: 'SUCCESS' | 'PENDING' | 'FAILED';
}

interface Wallet {
  id: string;
  balance: number;
  currency: string;
}

@Component({
  selector: 'app-wallet-rewards',
  templateUrl: './wallet-rewards.component.html',
  styleUrls: ['./wallet-rewards.component.css']
})
export class WalletRewardsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  showAddFunds: boolean = false;
  showRedeem: boolean = false;
  isLoading: boolean = false;
  fundAmount: number = 0;
  redeemAmount: number = 0;
  selectedPresetAmount: number = 0;
  wallet: Wallet | null = null;
  userId: string = '';
  userName: string = '';
  contact: string = '';
  readonly url: string = URLS.BASE_URL;
  readonly presetAmounts: number[] = [10, 100, 1000, 2000];
  readonly minAmount: number = 10;
  readonly maxAmount: number = 50000;
  transactionFilter: string = 'last10';
  sortFilter: string = 'wallet&rewards';
  transactions: Transaction[] = [];
  filteredTransactions: Transaction[] = [];

  constructor(
    private walletService: WalletinfoService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private toastrService: CustomControlsService
  ) {}

  ngOnInit(): void {
    this.initializeUserData();
    this.handleRouteParams();
    if (this.userId) {
      this.loadWalletData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeUserData(): void {
    this.userName = localStorage.getItem('screenName') || '';
    this.contact = localStorage.getItem('contact') || '';
    this.userId = localStorage.getItem('userId') || '';
  }

  private handleRouteParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['transactionId']) {
          this.handlePaymentCallback(params);
        }
      });
  }

  private handlePaymentCallback(params: any): void {
    const data = {
      vidLibId: '',
      message: 'Your payment for wallet topUp is in progress! Please wait for sometime.',
      transactionId: params['transactionId'],
      dateOfSubscription: new Date(),
      dateOfExpiry: new Date(),
      amount: params['amount'],
      detailsRoute: 'wallet'
    };
    this.showPaymentStatus(data);
  }

  private loadWalletData(): void {
    this.isLoading = true;
    this.getUserWallet(this.userId);
    this.getTransactions();
  }

  getUserWallet(userId: string): void {
    this.walletService.getWalletInfo(`wallet-id-${userId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.wallet = result;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading wallet:', error);
          this.isLoading = false;
          this.showToast('error', 'Failed to load wallet information');
        }
      });
  }

  getTransactions(): void {
    this.walletService.getUserTransaction()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.transactions = result;
          this.applyFilters();
        },
        error: (error) => {
          console.error('Error loading transactions:', error);
          this.showToast('error', 'Failed to load transaction history');
        }
      });
  }

  private applyFilters(): void {
    this.filteredTransactions = this.transactions.filter(transaction => {
      if (this.sortFilter === 'wallet') {
        return transaction.eventType === 'SUBSCRIPTION_FROM_WALLET' || transaction.eventType === 'WALLET_TOP_UP';
      } else if (this.sortFilter === 'rewards') {
        return transaction.eventType !== 'SUBSCRIPTION_FROM_WALLET' && transaction.eventType !== 'WALLET_TOP_UP';
      }
      return true; 
    });
    if (this.transactionFilter === 'last10') {
      this.filteredTransactions = this.filteredTransactions.slice(0, 10);
    }
  }

  sortTransactions(): void {
    this.applyFilters();
  }

  setPresetAmount(amount: number): void {
    this.fundAmount = amount;
    this.selectedPresetAmount = amount;
  }

  onAmountChange(): void {
    if (this.fundAmount !== this.selectedPresetAmount) {
      this.selectedPresetAmount = 0;
    }
  }

  addFunds(): void {
    if (!this.validateFundAmount()) {
      return;
    }
    this.isLoading = true;
    const requestBody = {
      userId: this.userId,
      amount: this.fundAmount.toString(),
      currency: 'INR',
      walletId: this.wallet?.id
    };
    this.walletService.topUpWallet(requestBody).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.openCashfreeCheckout(response);
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Top-up error:', err);
        this.showToast('error', 'Something went wrong! Please try again later.');
      }
    });
  }

  private validateFundAmount(): boolean {
    if (!this.fundAmount || this.fundAmount < this.minAmount) {
      this.showToast('error', `Minimum amount is ₹${this.minAmount}`);
      return false;
    }
    if (this.fundAmount > this.maxAmount) {
      this.showToast('error', `Maximum amount is ₹${this.maxAmount}`);
      return false;
    }    
    return true;
  }

  async openCashfreeCheckout(response: any): Promise<void> {
    const redirectUrl = `${window.location.href}?transactionId=${response.orderId}&amount=${this.fundAmount}`;
    try {
      const cashfree = await load({ mode: "sandbox" });
      const checkoutOptions = {
        paymentSessionId: response.paymentSessionId,
        appId: "61571b19abad01b50bc60940d17516",
        orderId: response.orderId,
        customerPhone: this.contact,
        customerName: this.userName,
        customerEmail: 'testuser@example.com',
        redirectTarget: "_self",
        returnUrl: redirectUrl
      };
      await cashfree.checkout(checkoutOptions);
    } catch (error) {
      console.error('Cashfree error:', error);
      this.showToast('error', 'Payment gateway error. Please try again.');
    }
  }

  redeemCoins(): void {
    if (!this.redeemAmount || this.redeemAmount <= 0) {
      this.showToast('error', 'Please enter a valid amount to redeem');
      return;
    }
    console.log(`Redeeming ${this.redeemAmount} coins`);
    this.showRedeem = false;
  }

  showPaymentStatus(data: any): void {
    this.dialog.open(PaymentStatusModalComponent, {
      disableClose: true,
      data: data,
      width: '400px',
      panelClass: 'payment-status-modal'
    });
  }

  private showToast(type: 'success' | 'error' | 'info', message: string): void {
    this.toastrService.showNotification(type, '', message, 5000);
  }

  getTransactionTypeLabel(eventType: string): string {
    switch (eventType) {
      case 'SUBSCRIPTION_FROM_WALLET':
        return 'Subscription Payment';
      case 'WALLET_TOP_UP':
        return 'Wallet Top Up';
      default:
        return 'Rewards';
    }
  }

  getTransactionIcon(eventType: string): string {
    if (eventType === 'SUBSCRIPTION_FROM_WALLET' || eventType === 'WALLET_TOP_UP') {
      return 'wallet';
    }
    return 'rewards';
  }

  getAmountClass(transaction: Transaction): string {
    if (transaction.eventType === 'SUBSCRIPTION_FROM_WALLET') {
      return 'failed';
    }
    if (transaction.eventType === 'WALLET_TOP_UP') {
      return transaction.transactionStatus.toLowerCase();
    }
    return 'success';
  }

  getStatusClass(status: string): string {
    return status.toLowerCase();
  }

  isPresetSelected(amount: number): boolean {
    return this.selectedPresetAmount === amount;
  }
}